import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Shirt, Footprints, Sparkles, Home as HomeIcon, Headphones, Flame,
  Truck, ShieldCheck, RotateCcw, Headset, Store,
} from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const CAT_ICONS = {
  fashion: Shirt,
  footwear: Footprints,
  beauty: Sparkles,
  "home-living": HomeIcon,
  gadgets: Headphones,
};

// Zilkart-branded hero slides (built in code, logo-green palette)
const SLIDES = [
  {
    key: "deals", to: "/products?featured=1", cats: null,
    bg: "from-[#04301b] via-[#0a4a27] to-[#1b6b24]",
    eyebrow: "Zilkart Value Days",
    title: "Premium finds. Honest prices.",
    cta: "Shop today's deals",
  },
  {
    key: "fashion", to: "/products?category=fashion", cats: ["fashion", "footwear"],
    bg: "from-[#06301f] via-[#0f5a3c] to-[#1d8157]",
    eyebrow: "Fashion & Footwear",
    title: "Style that speaks for itself",
    cta: "Explore fashion",
  },
  {
    key: "beauty", to: "/products?category=beauty", cats: ["beauty"],
    bg: "from-[#17330f] via-[#2a5a17] to-[#4a8a22]",
    eyebrow: "Beauty & Grooming",
    title: "Skincare, makeup & more",
    cta: "Shop beauty",
  },
  {
    key: "gadgets", to: "/products?category=gadgets", cats: ["gadgets", "home-living"],
    bg: "from-[#072a26] via-[#0f4d46] to-[#1b7a6c]",
    eyebrow: "Gadgets & Home",
    title: "Smart picks for everyday living",
    cta: "Shop now",
  },
];

// Deal rows
const ROWS = [
  { title: "Fashion & Footwear", sub: "Best value picks", cats: ["fashion", "footwear"], link: "/products?category=fashion" },
  { title: "Trending Gadgets", sub: "Tech worth having", cats: ["gadgets"], link: "/products?category=gadgets" },
  { title: "Beauty & Grooming", sub: "Glow with confidence", cats: ["beauty"], link: "/products?category=beauty" },
  { title: "Home & Living", sub: "Make it yours", cats: ["home-living"], link: "/products?category=home-living" },
];

function DealRow({ title, sub, link, products, testKey }) {
  if (!products.length) return null;
  return (
    <section className="surface" data-testid={`deal-row-${testKey || link}`}>
      <div className="flex items-end justify-between gap-4 px-5 sm:px-7 pt-5 sm:pt-6">
        <div>
          <h2 className="font-display text-2xl sm:text-[28px] font-bold text-deep leading-tight">{title}</h2>
          {sub && <p className="mt-0.5 text-sm text-muted-foreground">{sub}</p>}
        </div>
        <Link to={link} className="shrink-0 inline-flex items-center gap-1 text-sm font-bold text-primary hover:text-deep transition-colors">
          View all <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="p-4 sm:p-6 grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-5">
        {products.slice(0, 5).map((p, n) => (
          <div key={p.id} className={n === 4 ? "hidden xl:block h-full" : "h-full"}>
            <ProductCard product={p} />
          </div>
        ))}
      </div>
    </section>
  );
}

// Admin-managed banners (Content > Banners) replace the built-in slides when any are active
const BANNER_BG = {
  emerald: "from-[#04301b] via-[#0a4a27] to-[#1b6b24]",
  forest: "from-[#06301f] via-[#0f5a3c] to-[#1d8157]",
  teal: "from-[#072a26] via-[#0f4d46] to-[#1b7a6c]",
  olive: "from-[#17330f] via-[#2a5a17] to-[#4a8a22]",
};
const bannerToSlide = (b) => ({
  key: b.id, to: b.link || "/products", cats: null, bg: BANNER_BG[b.theme] || BANNER_BG.emerald,
  eyebrow: b.eyebrow, title: b.title, cta: b.cta || "Shop now", image: b.image_url,
});

function SlideLink({ to, children, ...rest }) {
  if (/^https?:\/\//.test(to)) return <a href={to} {...rest}>{children}</a>;
  return <Link to={to} {...rest}>{children}</Link>;
}

function Hero({ products }) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [cms, setCms] = useState([]);
  useEffect(() => { api.get("/cms/banners").then((r) => { setCms(r.data); setI(0); }).catch(() => {}); }, []);
  const slides = useMemo(() => (cms.length ? cms.map(bannerToSlide) : SLIDES), [cms]);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setI((n) => (n + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [paused, slides.length]);

  const maxOff = useMemo(() => {
    const offs = products
      .filter((p) => p.mrp && p.mrp > p.price)
      .map((p) => Math.round(((p.mrp - p.price) / p.mrp) * 100));
    return offs.length ? Math.max(...offs) : 0;
  }, [products]);

  const imagesFor = (s) => {
    const pool = s.cats ? products.filter((p) => s.cats.includes(p.category_slug)) : products;
    return pool.filter((p) => (p.images || [])[0]).slice(0, 3);
  };

  const go = (d) => setI((n) => (n + d + slides.length) % slides.length);

  return (
    <div
      className="relative overflow-hidden rounded-2xl shadow-[0_20px_50px_-24px_rgba(6,48,28,0.55)]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="home-banner-carousel"
    >
      <div className="flex transition-transform duration-700 ease-out" style={{ transform: `translateX(-${i * 100}%)` }}>
        {slides.map((s, idx) => {
          const imgs = imagesFor(s);
          return (
            <SlideLink
              key={s.key}
              to={s.to}
              className={`relative shrink-0 w-full h-[230px] sm:h-[300px] lg:h-[380px] bg-gradient-to-r ${s.bg} text-white flex items-center overflow-hidden`}
              data-testid={`home-banner-${idx}`}
              aria-hidden={idx !== i}
              tabIndex={idx === i ? 0 : -1}
            >
              <div className="absolute inset-0 hero-glow" />
              <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full border border-white/10" />
              <div className="absolute -right-8 -top-8 h-56 w-56 rounded-full border border-white/10" />
              <div className="relative z-10 flex-1 min-w-0 pl-6 sm:pl-[4.5rem] lg:pl-20 pr-3 sm:pr-6">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.28em] text-[#e9c46a]">{s.eyebrow}</p>
                <h2 className="mt-2 sm:mt-3 font-display text-[22px] leading-[1.15] sm:text-[34px] lg:text-[42px] xl:text-[50px] lg:leading-[1.12] font-bold">{s.title}</h2>
                {s.key === "deals" && maxOff > 0 && (
                  <p className="mt-2 sm:mt-3 text-xs sm:text-base text-white/80">Save up to <span className="font-bold text-[#e9c46a]">{maxOff}%</span> on selected products</p>
                )}
                <span className="inline-flex items-center gap-1 mt-4 sm:mt-7 bg-[#e9c46a] hover:bg-[#f0d17f] text-deep text-xs sm:text-sm font-bold px-5 sm:px-8 py-2.5 sm:py-3.5 rounded-full shadow-lg transition-colors">
                  {s.cta} <ChevronRight className="h-4 w-4" />
                </span>
              </div>
              <div className="relative z-10 shrink-0 flex items-center gap-3 sm:gap-5 pr-4 sm:pr-20 lg:pr-24">
                {s.image ? (
                  <div className="overflow-hidden rounded-2xl bg-white shadow-2xl ring-4 ring-white/90 h-28 w-40 sm:h-52 sm:w-64 lg:h-72 lg:w-80">
                    <img src={s.image} alt="" className="h-full w-full object-cover" />
                  </div>
                ) : imgs.map((p, n) => (
                  <div
                    key={p.id}
                    className={`overflow-hidden rounded-2xl bg-white shadow-2xl ring-4 ring-white/90 ${n === 0 ? "h-28 w-24 sm:h-52 sm:w-40 lg:h-72 lg:w-48" : n === 1 ? "hidden lg:block lg:h-56 lg:w-36 rotate-3" : "hidden xl:block xl:h-56 xl:w-36 -rotate-3"}`}
                  >
                    <img src={p.images[0]} alt="" className="h-full w-full object-contain p-2" loading="lazy" />
                  </div>
                ))}
              </div>
            </SlideLink>
          );
        })}
      </div>

      <button onClick={() => go(-1)} className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 h-11 w-11 items-center justify-center rounded-full bg-black/25 hover:bg-black/45 text-white backdrop-blur-sm transition-colors" aria-label="Previous banner" data-testid="hero-prev">
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button onClick={() => go(1)} className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 items-center justify-center rounded-full bg-black/25 hover:bg-black/45 text-white backdrop-blur-sm transition-colors" aria-label="Next banner" data-testid="hero-next">
        <ChevronRight className="h-5 w-5" />
      </button>

      <div className="absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {slides.map((s, n) => (
          <button
            key={s.key}
            onClick={() => setI(n)}
            className={`h-1.5 rounded-full transition-all ${n === i ? "w-7 bg-[#e9c46a]" : "w-1.5 bg-white/50"}`}
            aria-label={`Go to banner ${n + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const [all, feat, c] = await Promise.all([
          api.get("/products?limit=60&sort=newest"),
          api.get("/products?featured=1&limit=10"),
          api.get("/categories"),
        ]);
        if (!active) return;
        setProducts(all.data.items);
        setFeatured(feat.data.items);
        setCategories(c.data);
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, []);

  const byCat = (cats) => products.filter((p) => cats.includes(p.category_slug));
  const budget = useMemo(
    () => products.filter((p) => p.price <= 999 && p.stock > 0).sort((a, b) => a.price - b.price),
    [products],
  );

  return (
    <div className="shell py-5 sm:py-7 space-y-5 sm:space-y-7">
      {/* Category strip */}
      <div className="surface px-4 sm:px-8 py-5 sm:py-6">
        <div className="flex flex-wrap justify-center gap-x-6 sm:gap-x-12 gap-y-5">
          <Link to="/products" className="flex flex-col items-center gap-2.5 group w-[84px] sm:w-[100px]" data-testid="home-icon-all">
            <div className="h-[68px] w-[68px] sm:h-20 sm:w-20 rounded-full bg-deep text-[#e9c46a] flex items-center justify-center ring-2 ring-transparent group-hover:ring-primary ring-offset-2 transition-all">
              <Flame className="h-7 w-7 sm:h-8 sm:w-8" />
            </div>
            <span className="text-xs sm:text-[13px] font-semibold text-foreground text-center leading-tight">For You</span>
          </Link>
          {categories.map((c) => {
            const Icon = CAT_ICONS[c.slug] || Sparkles;
            return (
              <Link key={c.slug} to={`/products?category=${c.slug}`} className="flex flex-col items-center gap-2.5 group w-[84px] sm:w-[100px]" data-testid={`home-icon-${c.slug}`}>
                <div className="h-[68px] w-[68px] sm:h-20 sm:w-20 rounded-full bg-secondary text-primary flex items-center justify-center overflow-hidden ring-2 ring-transparent group-hover:ring-primary ring-offset-2 transition-all">
                  {c.image ? (
                    <img src={c.image} alt="" className="h-full w-full object-cover" loading="lazy" />
                  ) : (
                    <Icon className="h-7 w-7" />
                  )}
                </div>
                <span className="text-xs sm:text-[13px] font-semibold text-foreground text-center leading-tight group-hover:text-primary transition-colors">{c.name}</span>
              </Link>
            );
          })}
        </div>
      </div>

      <Hero products={products} />

      {featured.length > 0 && (
        <DealRow title="Deals of the Day" sub="Handpicked offers, updated daily" link="/products?featured=1" products={featured} />
      )}

      {loading ? (
        <div className="surface text-center py-16 text-muted-foreground">Loading products…</div>
      ) : (
        <>
          {ROWS.slice(0, 2).map((r) => <DealRow key={r.title} title={r.title} sub={r.sub} link={r.link} products={byCat(r.cats)} />)}
          {budget.length >= 3 && (
            <DealRow title="Budget Buys under ₹999" sub="Great quality that fits your budget" link="/products?sort=price_low" products={budget} testKey="budget" />
          )}
          {ROWS.slice(2).map((r) => <DealRow key={r.title} title={r.title} sub={r.sub} link={r.link} products={byCat(r.cats)} />)}
        </>
      )}

      {/* Seller CTA */}
      <Link
        to="/register?role=vendor"
        className="relative overflow-hidden flex flex-col sm:flex-row sm:items-center justify-between gap-5 rounded-2xl bg-gradient-to-r from-[#04301b] to-[#14602f] text-white px-6 sm:px-12 py-7 sm:py-9 shadow-[0_20px_50px_-24px_rgba(6,48,28,0.6)]"
        data-testid="home-seller-cta"
      >
        <div className="absolute inset-0 hero-glow" />
        <div className="relative flex items-center gap-4 sm:gap-5">
          <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-white/10 flex items-center justify-center shrink-0">
            <Store className="h-6 w-6 sm:h-7 sm:w-7 text-[#e9c46a]" />
          </div>
          <div>
            <p className="font-display text-xl sm:text-2xl font-bold">Sell on Zilkart</p>
            <p className="text-sm text-white/70 mt-0.5">Reach customers across India. Start your store today.</p>
          </div>
        </div>
        <span className="relative self-start sm:self-auto shrink-0 bg-[#e9c46a] hover:bg-[#f0d17f] text-deep text-sm font-bold px-7 py-3 rounded-full transition-colors">Become a seller</span>
      </Link>

      {/* Trust strip */}
      <div className="surface grid grid-cols-2 lg:grid-cols-4 gap-px bg-border overflow-hidden">
        {[
          { icon: Truck, t: "Free Delivery", d: "On every order" },
          { icon: ShieldCheck, t: "Secure Payments", d: "Your data stays safe" },
          { icon: RotateCcw, t: "Easy Returns", d: "Hassle-free policy" },
          { icon: Headset, t: "Customer Support", d: "We're here to help" },
        ].map((x) => (
          <div key={x.t} className="flex items-center gap-3 sm:gap-4 bg-card px-4 sm:px-7 py-5">
            <div className="h-11 w-11 shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
              <x.icon className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">{x.t}</p>
              <p className="text-xs text-muted-foreground">{x.d}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
